import { apiFetchBlob, apiFetch, buildQueryString } from '@/lib/api'
import type { components } from '@/types/api'

/** Nakit planı backend uçları (`/api/v1/cash/*`, docs/NAKIT-PLANI-PLAN.md). */
export type CashFirm = components['schemas']['CashFirm']
export type CashWeekly = components['schemas']['CashWeekly']
export type CashRow = components['schemas']['CashRow']
export type CashLine = components['schemas']['CashLine']
export type CashPeriod = components['schemas']['CashPeriod']
export type CashPosition = components['schemas']['CashPosition']
export type CashPlanning = components['schemas']['CashPlanning']
export type CashPlanCari = components['schemas']['CashPlanCari']
export type CashPlanItem = components['schemas']['CashPlanItem']
export type CashPart = components['schemas']['CashPart']
export type CashStripPoint = components['schemas']['CashStripPoint']
export type CashStrip = components['schemas']['CashStrip']
export type CashItemPlanWrite = components['schemas']['CashItemPlanWrite']
export type CashItemPlanSaved = components['schemas']['CashItemPlanSaved']
export type CashPlanBulkRow = components['schemas']['CashPlanBulkRow']
export type CashPlanBulkSaved = components['schemas']['CashPlanBulkSaved']
export type CashAllocate = components['schemas']['CashAllocate']
export type CashBulk = components['schemas']['CashBulk']
export type CashHistoryEntry = components['schemas']['CashHistoryEntry']
export type CashManualItem = components['schemas']['CashManualItemRead']
export type CashManualItemWrite = components['schemas']['CashManualItemWrite']
export type CashManualBulkRow = components['schemas']['CashManualBulkRow']
export type CashSettings = components['schemas']['CashSettingsRead']
export type CashSettingsWrite = components['schemas']['CashSettingsWrite']
export type CashType = components['schemas']['CashType']
export type CashImportResult = components['schemas']['CashImportResult']
export type CashSuggestion = components['schemas']['CashSuggestion']
export type CashCheque = components['schemas']['CashCheque']
export type CashCari = components['schemas']['CashCari']
export type CashEndorse = components['schemas']['CashEndorse']
export type CashEndorseResult = components['schemas']['CashEndorseResult']
export type CashChequeDecision = components['schemas']['CashChequeDecision']
export type CashHealth = components['schemas']['CashHealth']
export type CashHealthItem = components['schemas']['CashHealthItem']
export type CashDecision = components['schemas']['CashDecision']
export type CashRealizedItem = components['schemas']['CashRealizedItem']
export type CashUnplanned = components['schemas']['CashUnplanned']
export type CashPaymentList = components['schemas']['CashPaymentList']
export type CashPaymentRow = components['schemas']['CashPaymentRow']
export type CashPaymentTransition = components['schemas']['CashPaymentTransition']
export type CashIbanAction = components['schemas']['CashIbanAction']
export type CashTodo = components['schemas']['CashTodo']
export type CashParallel = components['schemas']['CashParallel']
export type CashVariance = components['schemas']['CashVariance']

export const cashApi = {
  firms: () => apiFetch<CashFirm[]>('/cash/firms'),
  weekly: (firm: string, scenario = 'baz') =>
    apiFetch<CashWeekly>(`/cash/weekly${buildQueryString({ firm, scenario })}`),

  strip: (firm: string) => apiFetch<CashStrip>(`/cash/strip${buildQueryString({ firm })}`),
  planning: (firm: string) => apiFetch<CashPlanning>(`/cash/planning${buildQueryString({ firm })}`),
  saveItem: (body: CashItemPlanWrite) =>
    apiFetch<CashItemPlanSaved>('/cash/planning/items', { method: 'PUT', body }),
  /** Izgaranın değişen kalem planları; hatalı satır varsa 400 `row_errors` (contracts §2.2). */
  bulkPlans: (firm: string, rows: CashPlanBulkRow[]) =>
    apiFetch<CashPlanBulkSaved[]>('/cash/planning/items/bulk', {
      method: 'POST',
      body: { firm, rows },
    }),
  allocate: (body: CashAllocate) =>
    apiFetch<{ updated: number }>('/cash/planning/allocate', { method: 'POST', body }),
  bulk: (body: CashBulk) =>
    apiFetch<{ updated: number }>('/cash/planning/bulk', { method: 'POST', body }),
  history: (firm: string, kind: string, key: string) =>
    apiFetch<CashHistoryEntry[]>(`/cash/history${buildQueryString({ firm, kind, key })}`),

  manualItems: (firm: string) =>
    apiFetch<CashManualItem[]>(`/cash/manual-items${buildQueryString({ firm })}`),
  createManual: (body: CashManualItemWrite) =>
    apiFetch<CashManualItem>('/cash/manual-items', { method: 'POST', body }),
  updateManual: (id: string, body: CashManualItemWrite) =>
    apiFetch<CashManualItem>(`/cash/manual-items/${id}`, { method: 'PUT', body }),
  /** Izgaranın değişen/yeni satırları; hatalı satır varsa 400 `row_errors` (contracts §2.2). */
  bulkManual: (firm: string, rows: CashManualBulkRow[]) =>
    apiFetch<CashManualItem[]>('/cash/manual-items/bulk', { method: 'POST', body: { firm, rows } }),
  /** Okunan sürümle siler; kalem o arada değiştiyse 409 `record_changed`. */
  deleteManual: (firm: string, id: string, version: number) =>
    apiFetch<void>(`/cash/manual-items/${id}${buildQueryString({ firm, version })}`, {
      method: 'DELETE',
    }),
  copyManual: (firm: string, id: string, months: number, increasePct: number) =>
    apiFetch<CashManualItem[]>(`/cash/manual-items/${id}/copy${buildQueryString({ firm })}`, {
      method: 'POST',
      body: { months, increase_pct: increasePct },
    }),
  suggest: (firm: string, type: string, period: string, description: string) =>
    apiFetch<CashSuggestion>(
      `/cash/manual-items/suggest${buildQueryString({ firm, type, period, description })}`,
    ),

  settings: (firm: string) => apiFetch<CashSettings>(`/cash/settings${buildQueryString({ firm })}`),
  saveSettings: (body: CashSettingsWrite) =>
    apiFetch<CashSettings>('/cash/settings', { method: 'PUT', body }),
  importExcel: (firm: string, file: File, replace: boolean) => {
    const form = new FormData()
    form.append('firm', firm)
    form.append('mode', replace ? 'replace' : 'new')
    form.append('file', file)
    return apiFetch<CashImportResult>('/cash/import', { method: 'POST', body: form })
  },

  cheques: (firm: string) => apiFetch<CashCheque[]>(`/cash/cheques${buildQueryString({ firm })}`),
  caris: (firm: string, q: string) =>
    apiFetch<CashCari[]>(`/cash/caris${buildQueryString({ firm, q })}`),
  endorse: (body: CashEndorse) =>
    apiFetch<CashEndorseResult>('/cash/cheques/endorse', { method: 'POST', body }),
  unendorse: (firm: string, chequeKey: string) =>
    apiFetch<void>('/cash/cheques/unendorse', {
      method: 'POST',
      body: { firm, cheque_key: chequeKey },
    }),
  chequeDecision: (body: CashChequeDecision) =>
    apiFetch<void>('/cash/cheques/decision', { method: 'PUT', body }),
  health: (firm: string) => apiFetch<CashHealth>(`/cash/health${buildQueryString({ firm })}`),
  decision: (body: CashDecision) => apiFetch<void>('/cash/decisions', { method: 'PUT', body }),
  realizedQueue: (firm: string) =>
    apiFetch<CashRealizedItem[]>(`/cash/realized-queue${buildQueryString({ firm })}`),
  realize: (firm: string, id: string, done: boolean, newDate: string | null) =>
    apiFetch<void>(`/cash/manual-items/${id}/realize`, {
      method: 'POST',
      body: { firm, done, new_date: newDate },
    }),

  paymentList: (firm: string, weekEnd?: string) =>
    apiFetch<CashPaymentList>(`/cash/payment-list${buildQueryString({ firm, week_end: weekEnd })}`),
  transition: (body: CashPaymentTransition) =>
    apiFetch<CashPaymentList>('/cash/payment-list/transition', { method: 'POST', body }),
  paymentListPdf: (firm: string, weekEnd: string) =>
    apiFetchBlob(`/cash/payment-list/pdf${buildQueryString({ firm, week_end: weekEnd })}`, {
      accept: 'application/pdf',
    }),
  saveIban: (firm: string, cariCode: string, iban: string) =>
    apiFetch<CashIbanAction>('/cash/iban', {
      method: 'PUT',
      body: { firm, cari_code: cariCode, iban },
    }),
  todo: (firm: string) => apiFetch<CashTodo[]>(`/cash/todo${buildQueryString({ firm })}`),
  parallel: (firm: string, file: File) => {
    const form = new FormData()
    form.append('firm', firm)
    form.append('file', file)
    return apiFetch<CashParallel>('/cash/parallel', { method: 'POST', body: form })
  },

  variance: (firm: string, weekEnd?: string) =>
    apiFetch<CashVariance>(`/cash/variance${buildQueryString({ firm, week_end: weekEnd })}`),
  takeSnapshot: (firm: string) =>
    apiFetch<{ id: string }>(`/cash/snapshots${buildQueryString({ firm })}`, { method: 'POST' }),
}
